/**
 * Legacy bridge: 40-value `notification_type` enum -> domain + subdomain.
 *
 * Rows created by the template pipeline carry `domain` and `subdomain`
 * themselves, and those always win. This map only fills them in for rows that
 * predate the catalogue (and for any list payload that has not yet been widened
 * to expose the template fields), so the grouped centre never drops a row into
 * an "Other" bucket.
 *
 * Every entry is transcribed from `backend/notifications/catalogue/` — the
 * `legacy_type` declared on each shipped template. Where one legacy value is
 * reused by several templates the most representative subdomain is used.
 *
 * @link domainMeta for the module gates each domain is subject to.
 */
import type { NotificationDomain, NotificationType } from '../../lib/api';

export interface LegacyTypeMeta {
  domain: NotificationDomain;
  subdomain: string;
}

/** Exhaustive — a new enum value fails the build until it is classified. */
export const LEGACY_TYPE_META: Record<NotificationType, LegacyTypeMeta> = {
  // --- purchasing ----------------------------------------------------------
  po_created: { domain: 'purchasing', subdomain: 'po_header' },
  po_status_changed: { domain: 'purchasing', subdomain: 'po_header' },
  po_received: { domain: 'purchasing', subdomain: 'po_header' },
  po_delivery_approaching: { domain: 'purchasing', subdomain: 'po_line' },
  po_delivery_overdue: { domain: 'purchasing', subdomain: 'po_line' },
  calloff_sent: { domain: 'purchasing', subdomain: 'call_off' },
  calloff_confirmed: { domain: 'purchasing', subdomain: 'call_off' },
  calloff_dispatched: { domain: 'purchasing', subdomain: 'call_off' },
  calloff_delivery_lines_ready: { domain: 'purchasing', subdomain: 'call_off' },
  calloff_delivered: { domain: 'purchasing', subdomain: 'call_off' },
  calloff_cancelled: { domain: 'purchasing', subdomain: 'call_off' },
  calloff_edited: { domain: 'purchasing', subdomain: 'call_off' },
  delivery_appeal_submitted: { domain: 'purchasing', subdomain: 'appeal' },
  delivery_appeal_responded: { domain: 'purchasing', subdomain: 'appeal' },
  contract_expiring: { domain: 'purchasing', subdomain: 'contract' },
  supplier_query_created: { domain: 'purchasing', subdomain: 'query' },
  supplier_query_responded: { domain: 'purchasing', subdomain: 'query' },

  // --- quality -------------------------------------------------------------
  ncr_created: { domain: 'quality', subdomain: 'ncr' },
  ncr_status_changed: { domain: 'quality', subdomain: 'ncr' },
  ncr_assigned: { domain: 'quality', subdomain: 'ncr' },

  // --- documents & compliance ---------------------------------------------
  document_uploaded: { domain: 'compliance', subdomain: 'document' },
  document_expiring: { domain: 'compliance', subdomain: 'document' },
  document_acceptance_required: { domain: 'compliance', subdomain: 'document' },
  questionnaire_assigned: { domain: 'compliance', subdomain: 'questionnaire' },
  supplier_qualification_invite: { domain: 'compliance', subdomain: 'questionnaire' },
  supplier_qualification_complete: { domain: 'compliance', subdomain: 'questionnaire' },
  supplier_rating_changed: { domain: 'compliance', subdomain: 'supplier' },
  audit_action_due_soon: { domain: 'compliance', subdomain: 'audit' },
  audit_action_overdue: { domain: 'compliance', subdomain: 'audit' },

  // --- onboarding ----------------------------------------------------------
  onboarding_invitation_sent: { domain: 'onboarding', subdomain: 'invitation' },
  onboarding_invitation_accepted: { domain: 'onboarding', subdomain: 'invitation' },
  onboarding_submitted: { domain: 'onboarding', subdomain: 'workflow' },
  onboarding_approved: { domain: 'onboarding', subdomain: 'workflow' },
  onboarding_declined: { domain: 'onboarding', subdomain: 'workflow' },
  onboarding_info_requested: { domain: 'onboarding', subdomain: 'workflow' },

  // --- rfq -----------------------------------------------------------------
  rfq_created: { domain: 'rfq', subdomain: 'rfq' },
  rfq_response_received: { domain: 'rfq', subdomain: 'rfq' },
  rfq_awarded: { domain: 'rfq', subdomain: 'rfq' },
  rfq_cancelled: { domain: 'rfq', subdomain: 'rfq' },
  rfq_revision_requested: { domain: 'rfq', subdomain: 'rfq' },

  // --- system --------------------------------------------------------------
  user_invitation_sent: { domain: 'system', subdomain: 'user' },
  user_invitation_accepted: { domain: 'system', subdomain: 'user' },
  user_mentioned: { domain: 'system', subdomain: 'user' },
  system_announcement: { domain: 'system', subdomain: 'announcement' },
  weekly_report: { domain: 'system', subdomain: 'report' },
};

/**
 * Display labels for the subdomains the catalogue ships. Server rows carry
 * `subdomain_display`, which wins; this is the fallback for legacy rows.
 */
const SUBDOMAIN_LABELS: Record<string, string> = {
  po_header: 'Purchase Orders',
  po_line: 'PO Lines',
  schedule: 'Schedules',
  call_off: 'Call-Off',
  grn: 'Goods Receipts',
  asn: 'Shipments',
  appeal: 'Delivery Appeals',
  contract: 'Contracts',
  query: 'Supplier Queries',
  message: 'Messages',
  po_message: 'Messages',
  otif: 'OTIF',
  stock_planning: 'Stock Planning',
  import: 'Imports',
  digest: 'Digests',
  ncr: 'NCRs',
  car: 'Corrective Actions',
  concession: 'Concessions',
  document: 'Documents',
  questionnaire: 'Questionnaires',
  audit: 'Audits',
  supplier: 'Supplier Performance',
  invitation: 'Invitations',
  workflow: 'Onboarding',
  rfq: 'RFQs',
  announcement: 'Announcements',
  user: 'Users & Access',
  report: 'Reports',
};

/** Title-cases an unmapped subdomain slug, e.g. `part_change` -> `Part Change`. */
function titleCase(slug: string): string {
  return slug
    .split(/[_.-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Human label for a subdomain slug. */
export function getSubdomainLabel(subdomain: string): string {
  if (!subdomain) return 'Other';
  const mapped = SUBDOMAIN_LABELS[subdomain];
  return mapped ?? titleCase(subdomain);
}

/** Domain + subdomain for a legacy row, defaulting to System for unknowns. */
export function getLegacyTypeMeta(type: string): LegacyTypeMeta {
  const meta = (LEGACY_TYPE_META as Record<string, LegacyTypeMeta | undefined>)[type];
  return meta ?? { domain: 'system', subdomain: 'announcement' };
}
